import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import DefaultImg from "../../../assets/img/default-image.svg";
import {
    DEFAULT_ITEMS_PER_PAGE,
    ITEMS_PER_PAGE_OPTIONS,
    MAX_VISIBLE_PAGES,
} from "../../../constants/app";
import { formatAllowance } from "../../../models/vacancy";
import {
    useAppDispatch as useDispatch,
    useAppSelector as useSelector,
} from "../../../store";
import {
    hideTransparentPreloaderAction,
    showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { resetReferooDocumentsAction } from "../../../store/superadmin/slice";
import { getCandidateData, getReferooDocumentThunk } from "../../../store/superadmin/thunks";
import {
    ICandidate,
    IPaginatedData,
    PaginationLink,
} from "../../../store/superadmin/types";
import { capitalizeName } from "../../../utils/stringUtils";
import Pagination from "../../Pagination/Pagination";
import CustomButton from "../../UI/Buttons/CustomButton";
import Eye from "../../UI/Icons/Eye";
import StarRating from "../../UI/StarRating";
import CandidateReviewModal from "./CandidateReviewModal/Index";

interface AllCandidatesProps {
  type: string;

  /**
   * List of candidates and their associated data.
   * This includes details such as candidate information, pagination data, etc.
   */
  candidatesList: IPaginatedData<ICandidate>;

  /**
   * Function to fetch all candidates.
   * This is typically used to refresh or load the list of candidates.
   */
  getAllCandidates: () => void;

  /**
   * Function to update the current page for approved candidates.
   * This is used for pagination purposes.
   */
  setCurrentApprovedPage: (value: number) => void;

  /**
   * The current page number for approved candidates.
   * This determines which page of candidates is currently being displayed.
   */
  currentApprovedPage: number;

  /**
   * Function to update the current page for approved candidates.
   */
  setApprovedPerPage: (value: number) => void;

  /**
   * The current page sort order for approved candidates.
   */
  setSortOrder: (value: string) => void;

  getCandidateQualification: (
    value: string,
    alternativePositions: any
  ) => string;

  totalList: number;
}

/**
 * The `AllCandidates` component is responsible for displaying a list of candidates
 * along with their details and actions. It includes functionalities for pagination,
 * viewing candidate details, and managing dropdown actions for each candidate.
 *
 * @component
 * @param {AllCandidatesProps} props - The props for the `AllCandidates` component.
 * @param {ICandidatesList} props.candidatesList - The list of candidates and their associated data.
 * @param {Function} props.getAllCandidates - A function to fetch all candidates.
 * @param {Function} props.setCurrentApprovedPage - A function to update the current page for approved candidates.
 * @param {number} props.currentApprovedPage - The current page number for approved candidates.
 *
 * @returns {JSX.Element} The rendered `AllCandidates` component.
 *
 * @remarks
 * - This component uses Redux for state management and dispatches actions to fetch candidate data.
 * - It includes a modal for reviewing candidate details and a dropdown for additional actions.
 * - Pagination is implemented to navigate through the list of candidates.
 *
 * @example
 * ```tsx
 * <AllCandidates
 *   candidatesList={candidatesList}
 *   getAllCandidates={fetchCandidates}
 *   setCurrentApprovedPage={setPage}
 *   currentApprovedPage={1}
 * />
 * ```
 */
const AllCandidates: FC<AllCandidatesProps> = ({
  type,
  candidatesList,
  setSortOrder,
  setApprovedPerPage,
  getAllCandidates,
  setCurrentApprovedPage,
  currentApprovedPage,
  getCandidateQualification,
  totalList
}) => {
  const dispatch = useDispatch();

  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [editCandidateId, setEditCandidateId] = useState<number>(0);
  const [candidatePrimaryId, setCandidatePrimaryId] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [approvedCandidateTotalPages, setApprovedCandidateTotalPages] =
    useState<number>(1);
  const [
    approvedCandidatePaginationLinks,
    setApprovedCandidatePaginationLinks,
  ] = useState<PaginationLink[]>([]);
  const reviewCandidateData = useSelector(
    (state) => state.superadmin.reviewCandidateData
  );

  const handleEdit = async (
    candidateId: number,
    candidateUUID: string,
    candidatePrimaryId: number
  ) => {
    setOpenDropdown(false);
    setEditCandidateId(candidateId);
    setCandidatePrimaryId(candidatePrimaryId);
    dispatch(showTransparentPreloaderAction());
    // const candidateResponse = await dispatch(
    //   getCandidateData({ candidate_id: candidateUUID })
    // );
    // dispatch(hideTransparentPreloaderAction());

    // if (isFulfilled(candidateResponse)) {
    //   if(reviewCandidateData?.referoo_status === 'complete') {
    //     await dispatch(getReferooDocumentThunk({ user_uuid: candidateUUID ?? "" }));
    //   }
    //   setShowModal(true);
    // } else {
    //   setShowModal(false);
    //   toast.error("Something went wrong while fetching candidate data.");
    // }

    try {
      const candidateResponse = await dispatch(
        getCandidateData({ candidate_id: candidateUUID })
      );

      if (isFulfilled(candidateResponse)) {
        const referooStatus = candidateResponse.payload?.data?.[0]?.referoo_status;

        if (referooStatus === 'complete') {
          const referooResponse = await dispatch(
            getReferooDocumentThunk({ user_uuid: candidateUUID ?? "" })
          );
          // Optional: check referooResponse status here
        }

        setShowModal(true); // Show modal only after all required data is ready
      } else {
        setShowModal(false);
        dispatch(resetReferooDocumentsAction()); // Reset referoo documents on error
        toast.error("Something went wrong while fetching candidate data.");
      }
    } catch (error) {
      setShowModal(false);
      dispatch(resetReferooDocumentsAction()); // Reset referoo documents on error
      toast.error("Unexpected error occurred.");
    } finally {
      dispatch(hideTransparentPreloaderAction()); // End loading
    }
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (
      candidatesList &&
      candidatesList?.data?.length > 0
    ) {
      setApprovedCandidateTotalPages(
        candidatesList?.last_page || 1
      );
      setApprovedCandidatePaginationLinks(candidatesList.links);
    }
  }, [candidatesList]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    let page_type = 'approved_page';
    if(type === 'compliance_alerts') {
      page_type = 'compliance_alerts_page';
    }
    if (url) {
      const page = new URL(url).searchParams.get(page_type);
      setCurrentApprovedPage(Number(page));
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {candidatesList.data?.map(
        (candidate: ICandidate, index: number) => {
          return (
            <div
              key={index}
              className={`bg-white p-15px d-flex gap-20 align-items-start align-items-lg-center justify-content-between ${
                candidatesList.data.length > 1
                  ? "border-bottom border-off-white"
                  : ""
              } fs-13 fw-400 text-black`}
            >
              <div className="flex-grow-1">
                <div className="d-flex flex-column flex-lg-row gap-20 gap-mobile-10 align-items-start align-items-lg-center">
                  <div className="flex-grow-1">
                    <div className="d-flex gap-20 align-items-center">
                      {candidate?.photo?.length === 0 ? (
                        <div className="bg-off-white min-w-50px w-50px h-50px rounded-circle"></div>
                      ) : (
                        <div className="w-50px h-50px  radius-50px">
                          <img
                            src={candidate?.photo || DefaultImg}
                            alt="Candidate"
                            width={"50px"}
                            height={"50px"}
                            className="img-responsive rounded-circle object-fit-cover"
                          />
                        </div>
                      )}
                      <div className="flex-grow-1 text-break">
                        <div className="d-flex flex-column gap-3 text-black">
                          <div className="fs-16 fw-700">
                            {capitalizeName(candidate?.first_name ?? "")} {capitalizeName(candidate?.last_name ?? "")}
                          </div>
                          <div className="fs-13 fw-400">
                            {candidate?.positions || ""}
                          </div>
                          { type === "compliance_alerts" && 
                            <div className="d-flex align-self-start py-4 px-8 bg-white border-1 border-solid border-alert radius-8px text-alert fs-13 fw-400">
                              {candidate.compliance_badge_label}
                            </div> 
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="min-w-105px">
                    <StarRating
                      rating={
                        candidate.average_rating ? candidate.average_rating : 0
                      }
                    />
                  </div>
                  <div className="min-w-110px d-flex flex-column gap-3">
                    <div className="fs-10 fw-400 text-grey-medium">
                      Past Jobs at Any Centre
                    </div>
                    <div className="fs-13 fw-400 text-black">
                      {candidate?.past_jobs_count_without_centre}{" "}
                      {candidate?.past_jobs_count_without_centre &&
                      candidate?.past_jobs_count_without_centre > 1
                        ? "Jobs"
                        : "Job"}
                    </div>
                  </div>
                  <div className="w-110px min-w-110px d-flex flex-column gap-3 text-break">
                    <div className="fs-10 fw-400 text-grey-medium">
                      Last Job at Any Centre
                    </div>
                    <div className="fs-13 fw-400 text-black">
                      {candidate?.last_job_date
                        ? candidate?.last_job_date
                        : "No Jobs Found"}
                      <span className="text-grey-medium">
                        {candidate?.last_job_centre
                          ? ` (${candidate?.last_job_centre})`
                          : ""}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-105px d-flex flex-column gap-3">
                    <div className="fs-10 fw-400 text-grey-medium">
                      Marked as Preferred at
                    </div>
                    <div className="fs-13 fw-400 text-black">
                      {candidate?.preferred_accounts || 0}{" "}
                      {candidate?.preferred_accounts > 1 ? "Centres" : "Centre"}
                    </div>
                  </div>
                  <div className="min-w-50px d-flex flex-column gap-3">
                    <div className="fs-10 fw-400 text-grey-medium">
                      Declined
                    </div>
                    <div className="fs-13 fw-400 text-black">
                      {candidate?.decline_jobs_count || 0}{" "}
                      {candidate?.decline_jobs_count > 1 ? "Jobs" : "Job"}
                    </div>
                  </div>
                  <div className="min-w-50px d-flex flex-column gap-3">
                    <div className="fs-10 fw-400 text-grey-medium">
                      Withdrawn
                    </div>
                    <div className="fs-13 fw-400 text-black">
                      {candidate?.withdrawn_jobs_count || 0}{" "}
                      {candidate?.withdrawn_jobs_count > 1 ? "Jobs" : "Job"}
                    </div>
                  </div>
                  <div className="min-w-50px d-flex flex-column gap-3">
                    <div className="fs-10 fw-400 text-grey-medium">Loading</div>
                    <div className="fs-13 fw-400 text-black">
                      {candidate?.loading_amount > 0 && "+"}
                      {formatAllowance(candidate?.loading_amount)}
                    </div>
                  </div>
                  <div className="min-w-158px">
                    <CustomButton
                      variant={"primary"}
                      className="btn-theme bg-teal text-black"
                      onClick={() =>
                        handleEdit(
                          candidate?.id,
                          candidate?.uuid,
                          candidate?.candidate_primary_key
                        )
                      }
                    >
                      { type === 'all_candidates' && <Eye color="black" /> }
                      { type === 'compliance_alerts' ? `Review` : `View Profile` }
                      { type === 'compliance_alerts' && 
                        <svg
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M14.1924 19.2071C13.8018 18.8166 13.8018 18.1834 14.1924 17.7929L18.4852 13.5L2.89942 13.5C2.34713 13.5 1.89942 13.0523 1.89942 12.5C1.89942 11.9477 2.34713 11.5 2.89942 11.5L18.4852 11.5L14.1924 7.2071C13.8018 6.81658 13.8018 6.18341 14.1924 5.79289C14.5829 5.40237 15.2161 5.40237 15.6066 5.7929L21.6065 11.7929C21.997 12.1834 21.997 12.8166 21.6065 13.2071L15.6066 19.2071C15.2161 19.5976 14.5829 19.5976 14.1924 19.2071Z"
                            fill="black"
                          />
                        </svg>
                      }
                    </CustomButton>
                  </div>
                </div>
              </div>
            </div>
          );
        }
      )}

      {totalList > DEFAULT_ITEMS_PER_PAGE ? (
        <div className="pt-10px">
          <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap">
            <div className="d-flex gap-3 align-items-center">
              <div className="fs-13 text-black text-nowrap">Items per page</div>
              <div className="form-group">
                <select
                  name=""
                  id=""
                  className="form-select text-only"
                  onChange={(e) => {
                    setApprovedPerPage(Number(e.target.value));
                    setCurrentApprovedPage(1); // If we are in page 2 and we change the items per page to 10, we need to go back to page 1.
                  }}
                >
                  {ITEMS_PER_PAGE_OPTIONS?.map((item, index) => {
                    return (
                      <option key={index} value={item}>
                        {item}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <Pagination
              currentPage={currentApprovedPage}
              totalPages={approvedCandidateTotalPages}
              paginationLinks={approvedCandidatePaginationLinks}
              maxVisiblePages={MAX_VISIBLE_PAGES}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      ) : (
        <></>
      )}

      {reviewCandidateData && (
        <CandidateReviewModal
          showModal={showModal}
          setShowModal={setShowModal}
          currentCandidateId={editCandidateId}
          reviewCandidateData={reviewCandidateData}
          getAllCandidates={getAllCandidates}
          currentCandidateUUID={reviewCandidateData?.uuid}
          currentCandidatePrimaryId={candidatePrimaryId}
          getCandidateQualification={getCandidateQualification}
        />
      )}
    </>
  );
};

export default AllCandidates;
